import { MutationHookOptions, QueryHookOptions } from '@apollo/client';
import {
  useApiLazyQuery,
  useApiMutation,
  useApiMutation_,
  useApiQuery,
  useApiUploader
} from '@common/hooks';
import { ErrorLogs } from '@common/types';

import {
  IGetActiveFacultativeDetailsByPolicyPlanCoverResponse,
  IGetActiveFacultativeDetailsByPolicyPlanCoverVariables
} from './types/getFacDetailsByPlanCover.types';
import {
  calculateAndSave,
  calculateBasicRate,
  checkActiveFacultativeDetailsExists,
  fetchedPersonAddressList,
  getActiveFacultativeDetailsByPolicyPlanCover,
  getBodyTypeList,
  getBusinessPartnerPersons,
  getMotorModelLovs,
  getPlanCoversList,
  getPolicyMedicalCovers,
  getProposalById,
  getProposalDescriptionOfRiskLovs,
  getProposalDetails,
  getProposalExpatLovs,
  getProposalFilters,
  getExportProposalLovs,
  exportSearchProposals,
  getProposalGroupMedicalPremiumDetails,
  getProposalMarineLovs,
  getProposalMedicalInsuredsLovs,
  getProposalMedicalLovs,
  getProposalMotorLovs,
  getProposalPolicyBusinessPartners,
  getProposalPremiumDetails,
  getProposalPropertyLovs,
  getProposalsList,
  getProposalTravelPremiumDetails,
  getTravelLovs,
  removePolicyPlanCover,
  saveDraft,
  updatePolicyCurrency,
  updatePolicyEffectiveDate,
  uploadMedicalInsuredList
} from './services';
import {
  CalculateBasicRateResponse,
  CalculateBasicRateVariables,
  CheckActiveFacultativeDetailsExistsResponse,
  CheckActiveFacultativeDetailsExistsVariables,
  GetBusinessPartnerPersonsResponse,
  GetBusinessPartnerPersonsVariables,
  GetExpatLovsResponse,
  GetMarineLovsResponse,
  GetMedicalInsuredsLovsResponse,
  GetMedicalLovsResponse,
  GetMedicalPlanCoversListResponse,
  GetMedicalPlanCoversListVariables,
  GetMotorBodyDetailsLovsVariables,
  GetMotorBodyLovsResponse,
  GetMotorDetailsLovsResponse,
  GetMotorModelLovsResponse,
  GetMotorModelsDetailsLovsVariables,
  GetPersonAddressListResponse,
  GetPersonAddressListVariables,
  GetPlanCoversListResponse,
  GetPlanCoversListVariables,
  GetProposalByIdResponse,
  GetProposalDescriptionOfRiskLovsResponse,
  GetProposalDetailsResponse,
  GetProposalDetailsVariables,
  GetProposalFiltersResponse,
  GetProposalFiltersVariables,
  GetExportProposalLovsResponse,
  GetExportProposalLovsVariables,
  ExportSearchProposalsResponse,
  ExportSearchProposalsVariables,
  GetProposalGroupMedicalPremiumDetailsResponse,
  GetProposalPolicyBusinessPartnersResponse,
  GetProposalPolicyBusinessPartnersVariables,
  GetProposalPremiumDetailsResponse,
  GetProposalPremiumDetailsVariables,
  GetProposalPropertyLovsResponse,
  GetProposalPropertyLovsVariables,
  GetProposalsListResponse,
  GetProposalsListVariables,
  GetProposalTravelPremiumDetailsResponse,
  GetTravelLovsResponse,
  GetTravelLovsVariables,
  ProposalIDVariables,
  RemovePolicyPlanCoverResponse,
  RemovePolicyPlanCoverVariables,
  SaveDraftGenericResponse,
  SaveDraftGenericVariables,
  UpdatePolicyCurrencyResponse,
  UpdatePolicyCurrencyVariables,
  UpdatePolicyEffectiveDateResponse,
  UpdatePolicyEffectiveDateVariables,
  UploadMedicalInsuredListResponse
} from './types';

const useGetProposals = (
  options?: QueryHookOptions<
    GetProposalsListResponse,
    GetProposalsListVariables
  >
) =>
  useApiQuery<GetProposalsListResponse, GetProposalsListVariables>(
    getProposalsList,
    'getProposals',
    { ...options, fetchPolicy: 'no-cache' },
    {
      withLoader: false
    }
  );

const useGetProposalFilters = (
  options?: QueryHookOptions<
    GetProposalFiltersResponse,
    GetProposalFiltersVariables
  >
) =>
  useApiQuery<GetProposalFiltersResponse, GetProposalFiltersVariables>(
    getProposalFilters,
    'getProposalFilters',
    {
      ...options,
      fetchPolicy: 'cache-and-network',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetExportProposalLovs = (
  options?: QueryHookOptions<
    GetExportProposalLovsResponse,
    GetExportProposalLovsVariables
  >
) =>
  useApiQuery<GetExportProposalLovsResponse, GetExportProposalLovsVariables>(
    getExportProposalLovs,
    'getExportProposalLovs',
    {
      ...options,
      fetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true
    },
    { withLoader: false }
  );

const useExportSearchProposals = (
  options?: QueryHookOptions<
    ExportSearchProposalsResponse,
    ExportSearchProposalsVariables
  >
) =>
  useApiLazyQuery<
    ExportSearchProposalsResponse,
    ExportSearchProposalsVariables
  >(
    exportSearchProposals,
    'exportSearchProposals',
    { ...options, fetchPolicy: 'no-cache' },
    { withLoader: false }
  );

const useGetProposalById = (
  options?: QueryHookOptions<GetProposalByIdResponse, ProposalIDVariables>
) =>
  useApiQuery<GetProposalByIdResponse, ProposalIDVariables>(
    getProposalById,
    'getProposalById',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetPlanCovers = (
  options?: QueryHookOptions<
    GetPlanCoversListResponse,
    GetPlanCoversListVariables
  >
) =>
  useApiQuery<GetPlanCoversListResponse, GetPlanCoversListVariables>(
    getPlanCoversList,
    'getPlanPlanCovers',
    { fetchPolicy: 'cache-and-network', ...(options ?? {}) }
  );

const useGetMedicalPlanCovers = (
  options?: QueryHookOptions<
    GetMedicalPlanCoversListResponse,
    GetMedicalPlanCoversListVariables
  >
) =>
  useApiQuery<
    GetMedicalPlanCoversListResponse,
    GetMedicalPlanCoversListVariables
  >(
    getPolicyMedicalCovers,
    'getPolicyMedicalCovers',
    {
      fetchPolicy: 'cache-and-network',
      ...(options ?? {})
    },
    { withLoader: false }
  );

const useGetMarineLovs = (options?: QueryHookOptions<GetMarineLovsResponse>) =>
  useApiQuery<GetMarineLovsResponse>(
    getProposalMarineLovs,
    'getProposalMarineLovs',
    { fetchPolicy: 'no-cache', ...(options ?? {}) }
  );

const useGetExpatLovs = (options?: QueryHookOptions<GetExpatLovsResponse>) =>
  useApiQuery<GetExpatLovsResponse>(
    getProposalExpatLovs,
    'getProposalExpatLovs',
    { fetchPolicy: 'no-cache', ...(options ?? {}) }
  );

const useGetPropertyLovs = (
  options?: QueryHookOptions<
    GetProposalPropertyLovsResponse,
    GetProposalPropertyLovsVariables
  >
) =>
  useApiQuery<
    GetProposalPropertyLovsResponse,
    GetProposalPropertyLovsVariables
  >(getProposalPropertyLovs, 'getProposalPropertyLovs', {
    fetchPolicy: 'no-cache',
    ...(options ?? {})
  });

const useGetDescriptionOfRiskLovs = (
  options?: QueryHookOptions<GetProposalDescriptionOfRiskLovsResponse>
) =>
  useApiQuery<GetProposalDescriptionOfRiskLovsResponse>(
    getProposalDescriptionOfRiskLovs,
    'getProposalDescriptionOfRiskLovs',
    {
      fetchPolicy: 'no-cache',
      ...(options ?? {})
    }
  );

const useGetMedicalLovs = (
  options?: QueryHookOptions<GetMedicalLovsResponse>
) =>
  useApiQuery<GetMedicalLovsResponse>(
    getProposalMedicalLovs,
    'getProposalMedicalLovs',
    { fetchPolicy: 'no-cache', ...(options ?? {}) },
    { withLoader: false }
  );

const useGetMedicalInsuredsLovs = (
  options?: QueryHookOptions<GetMedicalInsuredsLovsResponse>
) =>
  useApiQuery<GetMedicalInsuredsLovsResponse>(
    getProposalMedicalInsuredsLovs,
    'getProposalMedicalInsuredsLovs',
    { fetchPolicy: 'no-cache', ...(options ?? {}) }
  );

const useGetProposalDetails = (
  options?: QueryHookOptions<
    GetProposalDetailsResponse,
    GetProposalDetailsVariables
  >
) =>
  useApiQuery<GetProposalDetailsResponse, GetProposalDetailsVariables>(
    getProposalDetails,
    'getProposalDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useCalculateBasicRate = (
  options?: MutationHookOptions<
    CalculateBasicRateResponse,
    CalculateBasicRateVariables
  >
) =>
  useApiMutation<CalculateBasicRateResponse, CalculateBasicRateVariables>(
    calculateBasicRate,
    'calculateBasicRate',
    {
      withLoader: false
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useSaveDraftGeneric = (
  options?: MutationHookOptions<
    SaveDraftGenericResponse,
    SaveDraftGenericVariables
  >
) =>
  useApiMutation<SaveDraftGenericResponse, SaveDraftGenericVariables>(
    saveDraft,
    'saveDraft',
    {
      withLoader: false,
      successMsg: 'Proposal successfully saved!'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getProposalById]
    }
  );

const useCalculateAndSaveGeneric = (
  options?: MutationHookOptions<
    SaveDraftGenericResponse,
    SaveDraftGenericVariables
  >
) =>
  useApiMutation_<SaveDraftGenericResponse, SaveDraftGenericVariables>(
    calculateAndSave,
    'calculateAndSave',
    {
      withLoader: false,
      successMsg: 'Proposal successfully saved!'
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      refetchQueries: [getProposalById, getProposalDetails]
    },
    {
      mode: 'update'
    }
  );

const useGetBusinessPartnerPersons = (
  options?: QueryHookOptions<
    GetBusinessPartnerPersonsResponse,
    GetBusinessPartnerPersonsVariables
  >
) =>
  useApiQuery<
    GetBusinessPartnerPersonsResponse,
    GetBusinessPartnerPersonsVariables
  >(getBusinessPartnerPersons, 'fetchedPersons', {
    fetchPolicy: 'no-cache',
    ...(options ?? {})
  });

const useGetPersonAddressList = (
  options?: QueryHookOptions<
    GetPersonAddressListResponse,
    GetPersonAddressListVariables
  >
) =>
  useApiQuery<GetPersonAddressListResponse, GetPersonAddressListVariables>(
    fetchedPersonAddressList,
    'fetchedPersonAddressList',
    {
      fetchPolicy: 'network-only',
      ...(options ?? {})
    }
  );

const useGetProposalPolicyBusinessPartners = (
  options?: QueryHookOptions<
    GetProposalPolicyBusinessPartnersResponse,
    GetProposalPolicyBusinessPartnersVariables
  >
) =>
  useApiLazyQuery<
    GetProposalPolicyBusinessPartnersResponse,
    GetProposalPolicyBusinessPartnersVariables
  >(
    getProposalPolicyBusinessPartners,
    'getProposalPolicyBusinessPartners',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useCheckActiveFacultativeDetailsExists = (
  options?: QueryHookOptions<
    CheckActiveFacultativeDetailsExistsResponse,
    CheckActiveFacultativeDetailsExistsVariables
  >
) =>
  useApiLazyQuery<
    CheckActiveFacultativeDetailsExistsResponse,
    CheckActiveFacultativeDetailsExistsVariables
  >(
    checkActiveFacultativeDetailsExists,
    'checkActiveFacultativeDetailsExists',
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: false,
      ...(options ?? {})
    },
    { withLoader: false }
  );

const useRemovePolicyPlanCover = (
  coverName?: string,
  options?: MutationHookOptions<
    RemovePolicyPlanCoverResponse,
    RemovePolicyPlanCoverVariables
  >
) =>
  useApiMutation<RemovePolicyPlanCoverResponse, RemovePolicyPlanCoverVariables>(
    removePolicyPlanCover,
    'removePolicyPlanCover',
    {
      withLoader: false,
      successMsg: `Policy Plan Cover ${coverName} is removed successfully`
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetMotorLovs = (
  options?: QueryHookOptions<GetMotorDetailsLovsResponse>
) =>
  useApiQuery<GetMotorDetailsLovsResponse>(
    getProposalMotorLovs,
    'getProposalMotorLovs',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useGetMotorModelLovs = (
  options?: QueryHookOptions<
    GetMotorModelLovsResponse,
    GetMotorModelsDetailsLovsVariables
  >
) =>
  useApiLazyQuery<
    GetMotorModelLovsResponse,
    GetMotorModelsDetailsLovsVariables
  >(
    getMotorModelLovs,
    'getMotorModelLovs',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true // important!
    },
    {
      withLoader: false
    }
  );

const useGetMotorBodyLovs = (
  options?: QueryHookOptions<
    GetMotorBodyLovsResponse,
    GetMotorBodyDetailsLovsVariables
  >
) =>
  useApiLazyQuery<GetMotorBodyLovsResponse, GetMotorBodyDetailsLovsVariables>(
    getBodyTypeList,
    'getBodyTypeList',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetProposalPremiumDetails = (
  options?: QueryHookOptions<
    GetProposalPremiumDetailsResponse,
    GetProposalPremiumDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetProposalPremiumDetailsResponse,
    GetProposalPremiumDetailsVariables
  >(
    getProposalPremiumDetails,
    'getProposalPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetProposalTravelPremiumDetails = (
  options?: QueryHookOptions<
    GetProposalTravelPremiumDetailsResponse,
    GetProposalPremiumDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetProposalTravelPremiumDetailsResponse,
    GetProposalPremiumDetailsVariables
  >(
    getProposalTravelPremiumDetails,
    'getProposalTravelPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetProposalGroupMedicalPremiumDetails = (
  options?: QueryHookOptions<
    GetProposalGroupMedicalPremiumDetailsResponse,
    GetProposalPremiumDetailsVariables
  >
) =>
  useApiLazyQuery<
    GetProposalGroupMedicalPremiumDetailsResponse,
    GetProposalPremiumDetailsVariables
  >(
    getProposalGroupMedicalPremiumDetails,
    'getProposalGroupMedicalPremiumDetails',
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    },
    {
      withLoader: false
    }
  );

const useGetTravelLovs = (
  options?: QueryHookOptions<GetTravelLovsResponse, GetTravelLovsVariables>
) =>
  useApiQuery<GetTravelLovsResponse, GetTravelLovsVariables>(
    getTravelLovs,
    'getTravelLovs',
    {
      fetchPolicy: 'cache-and-network',
      ...(options ?? {})
    },
    { withLoader: false }
  );

const useUploadMedicalInsuredList = (
  proposalID: string,
  setPopUpOpen: React.Dispatch<React.SetStateAction<boolean>>,
  setErrorsLogs: React.Dispatch<React.SetStateAction<ErrorLogs[]>>
) => {
  const apiUploader = useApiUploader<
    UploadMedicalInsuredListResponse,
    ProposalIDVariables
  >();

  return async (file: File) =>
    apiUploader(uploadMedicalInsuredList, { proposalID }, file, {
      key: 'groupMedicalUpload',
      successMessage: 'File uploaded successfully!',
      successCondition: (body) =>
        body.data.production.uploadMedicalInsuredList.length === 0,
      onErrorCallback: (body) => {
        if (body.data) {
          setErrorsLogs(body.data?.production?.uploadMedicalInsuredList);
          setPopUpOpen(true);
        }
      }
    });
};

const useGetActiveFacultativeDetailsByPolicyPlanCover = (
  options?: QueryHookOptions<
    IGetActiveFacultativeDetailsByPolicyPlanCoverResponse,
    IGetActiveFacultativeDetailsByPolicyPlanCoverVariables
  >
) =>
  useApiLazyQuery<
    IGetActiveFacultativeDetailsByPolicyPlanCoverResponse,
    IGetActiveFacultativeDetailsByPolicyPlanCoverVariables
  >(
    getActiveFacultativeDetailsByPolicyPlanCover,
    'getActiveFacultativeDetailsByPolicyPlanCover',
    {
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: false,
      ...(options ?? {})
    },
    { withLoader: false }
  );

const useUpdatePolicyEffectiveDate = (
  options?: MutationHookOptions<
    UpdatePolicyEffectiveDateResponse,
    UpdatePolicyEffectiveDateVariables
  >
) =>
  useApiMutation<
    UpdatePolicyEffectiveDateResponse,
    UpdatePolicyEffectiveDateVariables
  >(
    updatePolicyEffectiveDate,
    'updatePolicyEffectiveDate',
    {
      withLoader: false,
      successMsg: `Policy Effective Date updated successfully.`
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

const useUpdatePolicyCurrency = (
  options?: MutationHookOptions<
    UpdatePolicyCurrencyResponse,
    UpdatePolicyCurrencyVariables
  >
) =>
  useApiMutation<UpdatePolicyCurrencyResponse, UpdatePolicyCurrencyVariables>(
    updatePolicyCurrency,
    'updatePolicyCurrency',
    {
      withLoader: false,
      successMsg: `Policy Currency updated successfully and values were reset.`
    },
    {
      ...options,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true
    }
  );

export {
  useCalculateAndSaveGeneric,
  useCalculateBasicRate,
  useCheckActiveFacultativeDetailsExists,
  useGetActiveFacultativeDetailsByPolicyPlanCover,
  useGetBusinessPartnerPersons,
  useGetDescriptionOfRiskLovs,
  useGetExpatLovs,
  useGetMarineLovs,
  useGetMedicalInsuredsLovs,
  useGetMedicalLovs,
  useGetMedicalPlanCovers,
  useGetMotorBodyLovs,
  useGetMotorLovs,
  useGetMotorModelLovs,
  useGetPersonAddressList,
  useGetPlanCovers,
  useGetPropertyLovs,
  useGetProposalById,
  useGetProposalDetails,
  useGetExportProposalLovs,
  useExportSearchProposals,
  useGetProposalFilters,
  useGetProposalGroupMedicalPremiumDetails,
  useGetProposalPolicyBusinessPartners,
  useGetProposalPremiumDetails,
  useGetProposals,
  useGetProposalTravelPremiumDetails,
  useGetTravelLovs,
  useRemovePolicyPlanCover,
  useSaveDraftGeneric,
  useUpdatePolicyCurrency,
  useUpdatePolicyEffectiveDate,
  useUploadMedicalInsuredList
};
